import { AfterViewInit, Component, Inject, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  AgGridLangMap,
  DocumentInfo,
  IconType,
  IoniconsFilled,
  IoniconsOutline,
  ND,
  UiDocumentType
} from '@pt/pt-models';
import {
  ListDataBreachHistoryResponse
} from 'it/primatoo/openapi/api/org/schemas/databreachprocess/ListDataBreachHistoryResponse';
import { TranslateService } from '@ngx-translate/core';
import { DocumentService } from '@pt/api-client';
import { OperationType, RequestLog } from '@primatoo-ms-organization';
import { ColDef, GridOptions } from 'ag-grid-community';
import { CustomRendererComponent } from '../../ag-grid/renderers/custom-renderer.component';
import { WidgetConfig } from '@pt/pt-core';
import { CustomDateFilterUtils } from '../../ag-grid/utils/custom-date-filter.utils';
import { ag_grid_frameworkComponents } from '../../pt-shared.constant';
import { FormControl, FormGroup, Validators } from '@angular/forms';


interface DurationItem {
  value: number;
  label: string;
}

@Component({
  selector: 'pt-document-history-dialog',
  templateUrl: 'document-history-dialog.component.html',
  styleUrls: [`document-history-dialog.component.scss`],
  encapsulation: ViewEncapsulation.None
})
export class DocumentHistoryDialogComponent implements AfterViewInit {
  @ViewChild('nameCol', { static: true }) nameCol!: TemplateRef<unknown>;
  @ViewChild('operationCol', { static: true }) operationCol!: TemplateRef<unknown>;

  public docInfo!: DocumentInfo;
  public loading = false;
  public history: ListDataBreachHistoryResponse = { items: [] };
  public readonly iconEmpty = 'library-outline';
  public readonly iconSize = 18;
  public readonly ND = ND;
  public data: RequestLog[] = [];
  public columnDefs: ColDef [] = [];
  public gridOptions: GridOptions;
  private agGridLangMap: AgGridLangMap;
  public IONIC_ICON: IconType = IconType.IONIC_ICON;
  public IconEmpty = IoniconsOutline.emptyStorage;
  public IconError = IoniconsOutline.warning;
  public IconReload = IoniconsFilled.refresh;
  public IconFind = IoniconsFilled.search;

  public IconOpen = IoniconsOutline.open;
  public type?: UiDocumentType;
  private readonly env;
  public from!: number;
  public duration!: number;
  public readonly DAY = 1000 * 60 * 60 * 24;
  public durationValue: DurationItem[] = [
    { label: '1 giorno', value: this.DAY },
    { label: '3 giorni', value: this.DAY * 3 },
    { label: '1 settimana', value: this.DAY * 7 },
    { label: '2 settimane', value: this.DAY * 7 * 2 },
    { label: '1 mese', value: this.DAY * 30 }
  ];

  public filterForm = new FormGroup({
    from: new FormControl(null, [Validators.required]),
    duration: new FormControl(null, [Validators.required])
  });

  public readonly OT = OperationType;
  public status: 'LOADING' | 'EMPTY' | 'DATA' | 'ERROR' = 'LOADING';

  constructor(
    @Inject(WidgetConfig) private config,
    public activeModal: NgbActiveModal,
    private documentService: DocumentService,
    private translateService: TranslateService
  ) {
    this.env = this.config.env;
    this.agGridLangMap = new AgGridLangMap(this.translateService.currentLang);

  }

  ngAfterViewInit(): void {
    this.columnDefs = [
      {
        field: 'resourceName',
        headerName: this.agGridLangMap.getOf('resourceName'),
        flex: 1,
        sortable: true,
        resizable: true,
        floatingFilter: true,
        filter: 'agTextColumnFilter',
        cellRenderer: CustomRendererComponent,
        cellRendererParams: { ngTemplate: this.nameCol }
      },
      {
        field: 'operationType',
        headerName: this.agGridLangMap.getOf('operationType'),
        flex: 0,
        width: 110,
        sortable: true,
        resizable: true,
        floatingFilter: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: ['DELETE', 'CREATE', 'UPDATE'],
          cellRenderer: 'enumToAdverbsFilterCellRendererComponent',
          suppressMiniFilter: true
        },
        floatingFilterComponent: 'enumToAdverbFilterComponent',
        cellRenderer: CustomRendererComponent,
        cellRendererParams: { ngTemplate: this.operationCol }
      },
      {
        field: 'count',
        headerName: this.agGridLangMap.getOf('count'),
        flex: 0,
        width: 100,
        sortable: true,
        resizable: true,
        floatingFilter: true,
        filter: 'agTextColumnFilter'
      },
      {
        field: 'username',
        headerName: this.agGridLangMap.getOf('username'),
        flex: 0,
        width: 110,
        sortable: true,
        resizable: true,
        floatingFilter: true,
        filter: 'agTextColumnFilter'
      },
      {
        field: 'from',
        headerName: this.agGridLangMap.getOf('data'),
        flex: 0,
        width: 110,
        sortable: true,
        resizable: true,
        cellRenderer: 'dateFormatterRenderer',
        ...CustomDateFilterUtils.customDataFilterColDefinition()
      },
      {
        field: 'chapter',
        headerName: this.agGridLangMap.getOf('chapter'),
        flex: 0,
        width: 130,
        sortable: true,
        resizable: true,
        floatingFilter: true,
        filter: 'agTextColumnFilter'
      }
    ];
    this.gridOptions = this.createGridOptions();
  }

  createGridOptions(): GridOptions {
    return {
      ...this.env.defaultGridOptions,
      enableRowGroup: true,
      suppressPropertyNamesCheck: true,
      components: ag_grid_frameworkComponents
    } as GridOptions;
  }

  reloadEventTable() {
    const from = this.filterForm.controls.from.value;
    const duration = this.filterForm.controls.duration.value;
    if (Number.isInteger(from) && Number.isInteger(duration)) {
      this.loadEvents(this.docInfo, from, duration);
    }
  }

  loadEvents(documentInfo: DocumentInfo, from: number, duration: number) {
    this.from = from;
    this.duration = duration;
    this.filterForm.setValue({
      from: from,
      duration: duration
    });
    this.status = 'LOADING';
    this.docInfo = documentInfo;
    this.type = DocumentService.getDocumentType(documentInfo.entity);
    if (this.type) {
      this.documentService.getLogs(documentInfo.docId, this.type, from, from + duration).subscribe((d: RequestLog[]) => {
          this.data = d;
          if (d.length > 0) {
            this.status = 'DATA';
          } else {
            this.status = 'EMPTY';
          }
        },
        () => {
          this.data = [];
          this.status = 'ERROR';
        });
    } else {
      console.warn('Invalid document entity ', documentInfo.entity);
      this.status = 'ERROR';
    }
  }
}
